/**
 * SYSTELOS MONITOR - Dashboard Principal
 * Visão geral do monitoramento DEC e DJE
 * 
 * @version 2.0.0
 * @date 24/01/2026
 */

import { useState, useEffect } from 'react'
import { 
  Users, 
  FileText, 
  AlertTriangle, 
  CheckCircle,
  RefreshCw,
  ChevronRight,
  Plus,
  Building2,
  Scale,
  Shield,
  Clock,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '@/components/layout/AppLayout'
import { PageContainer, PageBanner, PageSection, StatCard } from '@/components/page'
import { useAuth } from '@/contexts/AuthContext'
import clientesService from '@/services/clientesService'
import toast from 'react-hot-toast'

interface DashboardStats {
  totalClientes: number
  clientesAtivos: number
  pendenciasNovas: number
  errosConsulta: number
  certificadosExpirando: number
}

interface UltimaConsulta {
  data: string
  horarioDec: string
  horarioDje: string
  empresasVerificadas: number
  sucesso: number
  erros: number
  novasDec: number
  novasDje: number
}

interface Alerta {
  id: string
  cnpj: string
  razaoSocial: string
  tipo: 'DEC' | 'DJE' | 'CERTIFICADO'
  mensagem: string
  severidade: 'erro' | 'aviso'
}

export default function Dashboard() {
  const { usuario } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats>({
    totalClientes: 0,
    clientesAtivos: 0,
    pendenciasNovas: 0,
    errosConsulta: 0,
    certificadosExpirando: 0,
  })
  const [ultimaConsulta, setUltimaConsulta] = useState<UltimaConsulta | null>(null)
  const [alertas, setAlertas] = useState<Alerta[]>([])

  // Carregar dados da API
  const carregarDados = async () => {
    setLoading(true)
    try {
      const statsRes = await clientesService.stats()
      
      if (statsRes.data) {
        setStats({
          totalClientes: statsRes.data.total || 0,
          clientesAtivos: statsRes.data.ativos || 0,
          pendenciasNovas: statsRes.data.pendenciasNovas || 0,
          errosConsulta: statsRes.data.errosConsulta || 0,
          certificadosExpirando: statsRes.data.certificadosExpirando || 0,
        })

        // Montar última consulta se houver clientes
        if (statsRes.data.total > 0 && statsRes.data.ultimaConsulta) {
          setUltimaConsulta({
            data: new Date(statsRes.data.ultimaConsulta.data).toLocaleDateString('pt-BR'),
            horarioDec: statsRes.data.ultimaConsulta.horarioDec || '01:00 - 01:45',
            horarioDje: statsRes.data.ultimaConsulta.horarioDje || '02:00 - 02:15',
            empresasVerificadas: statsRes.data.ultimaConsulta.empresasVerificadas || statsRes.data.total,
            sucesso: statsRes.data.ultimaConsulta.sucesso || statsRes.data.ativos,
            erros: statsRes.data.ultimaConsulta.erros || 0,
            novasDec: statsRes.data.ultimaConsulta.novasDec || 0,
            novasDje: statsRes.data.ultimaConsulta.novasDje || 0,
          })
        }

        // Montar alertas
        const alertasTemp: Alerta[] = []
        
        if (statsRes.data.alertas && Array.isArray(statsRes.data.alertas)) {
          alertasTemp.push(...statsRes.data.alertas)
        } else {
          // Alertas genéricos baseados nos stats
          if ((statsRes.data.errosConsulta ?? 0) > 0) {
            alertasTemp.push({
              id: 'erro-consulta',
              cnpj: '--',
              razaoSocial: `${statsRes.data.errosConsulta} cliente(s)`,
              tipo: 'DEC',
              mensagem: 'Erro na última consulta',
              severidade: 'erro',
            })
          }
          
          if ((statsRes.data.certificadosExpirando ?? 0) > 0) {
            alertasTemp.push({
              id: 'cert-expirando',
              cnpj: '--',
              razaoSocial: `${statsRes.data.certificadosExpirando} cliente(s)`,
              tipo: 'CERTIFICADO',
              mensagem: 'Certificado expirando em breve',
              severidade: 'aviso',
            })
          }
        }
        
        setAlertas(alertasTemp)
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
      toast.error('Erro ao carregar dados do dashboard')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    carregarDados()
  }, [])

  const handleRefresh = async () => {
    await carregarDados()
    toast.success('Dados atualizados')
  }

  // Formatar data para exibição
  const formatarDataAtual = () => {
    return new Date().toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long' 
    })
  }

  // Obter ícone do tipo de alerta
  const getIconeTipo = (tipo: string) => {
    switch (tipo) {
      case 'DEC': return Building2
      case 'DJE': return Scale
      case 'CERTIFICADO': return Shield
      default: return AlertTriangle
    }
  }

  return (
    <AppLayout pagina="Dashboard">
      <PageContainer>
        {/* Banner */}
        <PageBanner
          titulo={`Olá, ${usuario?.nome?.split(' ')[0] || 'Usuário'}`}
          subtitulo={`SYSTELOS MONITOR - ${formatarDataAtual()}`}
          acoes={
            <PageBanner.BotaoSecundario 
              icon={<RefreshCw className="w-4 h-4" />} 
              onClick={handleRefresh}
              loading={loading}
            >
              Atualizar
            </PageBanner.BotaoSecundario>
          }
        />

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
            <span className="ml-3 text-gray-500">Carregando dados...</span>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <StatCard.Grid colunas={{ mobile: 2, tablet: 2, desktop: 4 }}>
              <StatCard
                label="Clientes Monitorados"
                valor={stats.totalClientes}
                icon={Users}
                cor="blue"
                onClick={() => navigate('/clientes')}
              />
              <StatCard
                label="Pendências Novas"
                valor={stats.pendenciasNovas}
                icon={FileText}
                cor="amber"
                onClick={() => navigate('/comunicacoes?status=NOVA')}
              />
              <StatCard
                label="Erros de Consulta"
                valor={stats.errosConsulta}
                icon={AlertTriangle}
                cor="red"
                onClick={() => navigate('/clientes?erro=true')}
              />
              <StatCard
                label="Cert. Expirando"
                valor={stats.certificadosExpirando}
                icon={Shield}
                cor="gray"
                onClick={() => navigate('/clientes?certificado=expirando')}
              />
            </StatCard.Grid>

            {/* Mensagem quando não há clientes */}
            {stats.totalClientes === 0 && (
              <PageSection>
                <div className="text-center py-8">
                  <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Nenhum cliente cadastrado ainda
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Cadastre seus clientes para começar o monitoramento do DEC e DJE.
                  </p>
                  <button
                    onClick={() => navigate('/clientes')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: '#FFB800', color: '#0F1B3D' }}
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar Primeiro Cliente
                  </button>
                </div>
              </PageSection>
            )}

            {/* Última Consulta Automática */}
            {stats.totalClientes > 0 && ultimaConsulta && (
              <PageSection
                titulo="Última Consulta Automática"
                subtitulo="Resumo da execução noturna"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Info da consulta */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Data</span>
                      <span className="font-medium text-gray-900">{ultimaConsulta.data}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Horário DEC</span>
                      <span className="font-medium text-gray-900">{ultimaConsulta.horarioDec}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Horário DJE</span>
                      <span className="font-medium text-gray-900">{ultimaConsulta.horarioDje}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Empresas verificadas</span>
                      <span className="font-medium text-gray-900">{ultimaConsulta.empresasVerificadas}</span>
                    </div>
                  </div>

                  {/* Resultados */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Sucesso</span>
                      <span className="font-medium text-emerald-600">{ultimaConsulta.sucesso}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Erros</span>
                      <span className={`font-medium ${ultimaConsulta.erros > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {ultimaConsulta.erros}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Novas DEC</span>
                      <span className={`font-medium ${ultimaConsulta.novasDec > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                        {ultimaConsulta.novasDec}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Novas DJE</span>
                      <span className={`font-medium ${ultimaConsulta.novasDje > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                        {ultimaConsulta.novasDje}
                      </span>
                    </div>
                  </div>
                </div>
              </PageSection>
            )}

            {/* Alertas */}
            {stats.totalClientes > 0 && (
              <PageSection
                titulo="Alertas"
                subtitulo="Itens que requerem atenção"
              >
                {alertas.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                    <p className="text-gray-600 font-medium">Nenhum alerta no momento</p>
                    <p className="text-gray-400 text-sm">Todas as consultas estão em dia.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 bg-gray-50">
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Cliente</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Tipo</th>
                          <th className="text-left px-4 py-3 text-sm font-semibold text-gray-600">Mensagem</th>
                          <th className="text-right px-4 py-3 text-sm font-semibold text-gray-600"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertas.map((alerta) => {
                          const IconeTipo = getIconeTipo(alerta.tipo)
                          return (
                            <tr 
                              key={alerta.id} 
                              className="border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer"
                              onClick={() => navigate('/clientes')}
                            >
                              <td className="px-4 py-3">
                                <span className="font-medium text-gray-900">{alerta.razaoSocial}</span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                                  alerta.tipo === 'DEC' 
                                    ? 'bg-blue-50 text-blue-700' 
                                    : alerta.tipo === 'DJE'
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  <IconeTipo className="w-3 h-3" />
                                  {alerta.tipo}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-sm ${
                                  alerta.severidade === 'erro' ? 'text-red-600' : 'text-amber-600'
                                }`}>
                                  {alerta.mensagem}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <ChevronRight className="w-5 h-5 text-gray-400 inline" />
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </PageSection>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <button
                onClick={() => navigate('/clientes')}
                className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#FFB800] hover:shadow-md transition-all text-left group"
              >
                <Users className="w-8 h-8 text-[#0F1B3D] mb-2 group-hover:text-[#14B8A6] transition-colors" />
                <h3 className="font-semibold text-gray-900">Gerenciar Clientes</h3>
                <p className="text-sm text-gray-500">Cadastrar e editar clientes</p>
              </button>

              <button
                onClick={() => navigate('/comunicacoes')}
                className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#FFB800] hover:shadow-md transition-all text-left group"
              >
                <FileText className="w-8 h-8 text-[#0F1B3D] mb-2 group-hover:text-[#14B8A6] transition-colors" />
                <h3 className="font-semibold text-gray-900">Ver Comunicações</h3>
                <p className="text-sm text-gray-500">Intimações e citações</p>
              </button>

              <button
                onClick={() => navigate('/configuracoes')}
                className="p-4 bg-white rounded-xl border border-gray-200 hover:border-[#FFB800] hover:shadow-md transition-all text-left group"
              >
                <Clock className="w-8 h-8 text-[#0F1B3D] mb-2 group-hover:text-[#14B8A6] transition-colors" />
                <h3 className="font-semibold text-gray-900">Configurações</h3>
                <p className="text-sm text-gray-500">Ajustar notificações</p>
              </button>
            </div>
          </>
        )}
      </PageContainer>
    </AppLayout>
  )
}

